/**
 * React Query hooks for recycle bin API.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type {
  BulkActionRequest,
  BulkActionResponse,
  EmptyBinResponse,
  RecycleBinItemType,
  RecycleBinListResponse,
} from '../types';

export interface RecycleBinParams {
  page?: number;
  page_size?: number;
  item_type?: RecycleBinItemType;
  deleted_after?: string;
  deleted_before?: string;
}

export const recycleBinKeys = {
  all: (tenantId: string | undefined) => ['recycle-bin', tenantId] as const,
  list: (tenantId: string | undefined, params: RecycleBinParams) =>
    ['recycle-bin', tenantId, 'list', params] as const,
};

export function useRecycleBin(params: RecycleBinParams = {}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: recycleBinKeys.list(tenantId, params),
    queryFn: async (): Promise<RecycleBinListResponse> => {
      const { data } = await apiClient.get<RecycleBinListResponse>(
        '/api/v1/recycle-bin',
        { params },
      );
      return data;
    },
    enabled: !!tenantId,
    staleTime: 30 * 1000,
  });
}

// ---------- mutations ----------

export function useRestoreItem() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async ({ itemType, itemId }: { itemType: RecycleBinItemType; itemId: string }) => {
      const { data } = await apiClient.post(
        `/api/v1/recycle-bin/${itemType}/${itemId}/restore`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recycleBinKeys.all(tenantId) });
    },
  });
}

export function useHardDeleteItem() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async ({ itemType, itemId }: { itemType: RecycleBinItemType; itemId: string }) => {
      await apiClient.delete(`/api/v1/recycle-bin/${itemType}/${itemId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recycleBinKeys.all(tenantId) });
    },
  });
}

export function useBulkAction() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (request: BulkActionRequest): Promise<BulkActionResponse> => {
      const { data } = await apiClient.post<BulkActionResponse>(
        '/api/v1/recycle-bin/bulk',
        request,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recycleBinKeys.all(tenantId) });
    },
  });
}

export function useEmptyBin() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (): Promise<EmptyBinResponse> => {
      // apiClient.delete types response as void; cast the underlying data
      const response = await apiClient.delete('/api/v1/recycle-bin');
      const data = response.data as unknown as EmptyBinResponse | undefined;
      return data ?? { deleted_count: 0 };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recycleBinKeys.all(tenantId) });
    },
  });
}

/**
 * Lightweight hook that returns only the total count of items in the recycle bin.
 * Used by the sidebar badge to show pending deleted items.
 */
export function useRecycleBinCount(): number {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  const { data } = useQuery({
    queryKey: [...recycleBinKeys.list(tenantId, { page_size: 1 }), 'count'],
    queryFn: async (): Promise<number> => {
      const { data: resp } = await apiClient.get<RecycleBinListResponse>(
        '/api/v1/recycle-bin',
        { params: { page_size: 1 } },
      );
      return resp.meta?.total ?? 0;
    },
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });

  return data ?? 0;
}

// =============================================================================
// Global conversation recycle bin (REQ-CHAT-020 — Studio surface, read-only)
// =============================================================================

/** Conversation row in the tenant-wide recycle bin (Studio). */
export interface GlobalConversationBinItem {
  id: string;
  agent_id: string | null;
  title: string | null;
  message_count: number;
  archived_at: string | null;
  purge_at: string | null;
  user_id: string | null;
  user_email: string | null;
}

export interface GlobalConversationBinResponse {
  data: GlobalConversationBinItem[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * List the tenant-wide conversation recycle bin (read-only). Requires the
 * `Module:conversations@{tenant}#bin_viewers` permission — a 403 means the
 * section must not be rendered.
 */
export function useGlobalConversationBin(page = 1, pageSize = 20) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: ['recycle-bin', tenantId, 'conversations', 'global', page, pageSize],
    queryFn: async (): Promise<GlobalConversationBinResponse> => {
      const { data } = await apiClient.get<GlobalConversationBinResponse>(
        `/api/v1/tenants/${tenantId}/conversations/recycle-bin/global`,
        { params: { page, page_size: pageSize } },
      );
      return data;
    },
    enabled: !!tenantId,
    retry: false,
    staleTime: 30 * 1000,
  });
}
